import {Injectable} from '@angular/core';
import {SearchResultEntry} from "../../../api/models/search-result-entry";
import {SearchResult} from "../../../api/models/search-result";
import {Meta, MetaDefinition} from "@angular/platform-browser";
import {INIAnalytics, NIAnalyticsWrapper} from "./ni-analytics-wrapper";
import {SearchDym} from "../../../api/models/search-dym";
import {TranslateService} from "@ngx-translate/core";
import {CookieService} from 'ngx-cookie-service';

declare var digitalData: any;

@Injectable({
  providedIn: 'root'
})
export class AnalyticsService {

  private niAnalytics: INIAnalytics = new NIAnalyticsWrapper();

  constructor(private metaService: Meta, private translateService: TranslateService, private cookieService: CookieService) {
  }

  trackVirtualPageLoad(initialized: boolean, shortTitle: string, searchResult: SearchResult) {
    if (!!window.monitoringTool) {
      console.info("Analytics sending is refused because of Monitoring-Tool header: " + window.monitoringTool);
      return;
    }

    if (searchResult.searchDym?.dymActive) {
      this.createOrUpdateTag({name: "ShortTitle", content: `${shortTitle}: ${searchResult.searchDym?.originalTerm}`});
      this.createOrUpdateTag({name: "EnglishShortTitle", content: `Search Results: ${searchResult.searchDym?.originalTerm}`});
    } else {
      this.createOrUpdateTag({name: "ShortTitle", content: shortTitle});
      this.createOrUpdateTag({name: "EnglishShortTitle", content: "Search - NI"});
    }
    this.createOrUpdateTag({name: "NI_SearchResultsCount", content: `${searchResult.pagination?.totalResults}`});
    this.createOrUpdateTag({name: "NI_SearchDYM", content: `${searchResult.searchDym?.suggestedTerm}`});
    this.createOrUpdateTag({name: "NI_SearchLanguage", content: `${this.translateService.currentLang}`});
    this.createOrUpdateTag({name: "Language", scheme: "rfc3066", content: `${this.translateService.currentLang}`});

    if (typeof digitalData !== "undefined") {
      try {
        if (!digitalData.onsiteSearch) {
          digitalData["onsiteSearch"] = {
            onsiteSearchInfo: {}
          };
        }

        digitalData.onsiteSearch.onsiteSearchInfo.onsiteSearchTerm = searchResult.searchDym?.termForAnalytics || "";
        digitalData.onsiteSearch.onsiteSearchInfo.onsiteSearchDym = searchResult.searchDym?.suggestedTerm;
        digitalData.onsiteSearch.onsiteSearchInfo.onsiteSearchScenario = searchResult.categoryFacet?.mostSpecificCategoryInEnglish;

      } catch (e) {
        console.error('Error while setting up digitalData', e);
      }
    }

    this.niAnalytics.captureVirtualPageLoad({forceSend: !initialized});

    let tntSearchExperience = this.cookieService.get('tnt_search_experience');
    if (tntSearchExperience) {
      this.niAnalytics.setAndTrack("eVar112", `2855552|impression|Experience ${tntSearchExperience}|Virtual page load`);
      this.niAnalytics.sendBeacon({"asPageView": false, "linkType": "o", "linkName": "o"});
    }
  }

  private createOrUpdateTag(tag: MetaDefinition) {
    try {
      let selector: string = 'name="' + tag.name + '"';
      let existingTag = this.metaService.getTag(selector);
      if (existingTag) {
        this.metaService.updateTag(tag, selector);
      } else {
        this.metaService.addTag(tag);
      }
    } catch (e) {
      console.error('Failed to create or update metaTag', tag, e);
    }
  }

  trackResultClick(url: string, result: SearchResultEntry, englishDisplayString: string) {
    if (!!window.monitoringTool) {
      console.info("Analytics sending is refused because of Monitoring-Tool header: " + window.monitoringTool);
      return;
    }

    const clickTitle = englishDisplayString?.trim()
      ? `${result.titleForAnalytics} - ${englishDisplayString}`
      : result.titleForAnalytics;

    this.niAnalytics.setAndTrack('eVar1', this.niAnalytics.getDataElement('PAGE_NAME'));
    this.niAnalytics.setAndTrack('eVar2', this.niAnalytics.getDataElement('ONSITESEARCHTERM:OSI:OS:DL'));
    this.niAnalytics.setAndTrack('eVar46', `${result.documentNumber}`);
    this.niAnalytics.setAndTrack('eVar47', clickTitle ?? '');
    this.niAnalytics.setAndTrack('eVar127', this.niAnalytics.getDataElement('CLICKTITLEURL:OSI:OS:DL'));
    this.niAnalytics.setAndTrack('event38', '');
    window.s.getPreviousValue(window.s.eVar2, 'gpv_ps', '');
    this.niAnalytics.sendBeacon({"asPageView": false, "linkType": "o", "linkName": "onsiteSearchClick"});

    let tntSearchExperience = this.cookieService.get('tnt_search_experience');
    if (tntSearchExperience) {
      this.niAnalytics.setAndTrack("eVar112", `2855552|click|Experience ${tntSearchExperience}|Result click`);
      this.niAnalytics.sendBeacon({"asPageView": false, "linkType": "o", "linkName": "o"});
    }
  }

  trackNavigationChange(sn?: string, facetCodeNames?: {[key: string]: string;}, facetValueCodeNames?: {[key: string]: string;}, facetValueEnglishDisplayString?: string) {
    if (!!window.monitoringTool) {
      console.info("Analytics sending is refused because of Monitoring-Tool header: " + window.monitoringTool);
      return;
    }

    if (sn == undefined || facetCodeNames == undefined || facetValueCodeNames == undefined) {
      return;
    }

    let filters: (string | undefined)[] = [];
    let facetCodeNameMap = new Map(Object.entries(facetCodeNames));
    let facetValueCodeNameMap = new Map(Object.entries(facetValueCodeNames));
    let snSplitted = sn.split(",");

    snSplitted.forEach((value) => {
      if (value.startsWith("catnav")) {
        let codesSplitted = value.split(":")[1].split(".");
        codesSplitted.forEach((code) => {
          filters.push(facetValueCodeNameMap.get("catnav:" + code));
          filters.push(":");
        });

      } else {
        filters.push(facetCodeNameMap.get(value.split(":")[0]));
        filters.push(":");
        filters.push(facetValueCodeNameMap.get(value));
        filters.push(":");
      }
    });

    let filtersJoined = filters.join("");

    if (filtersJoined.endsWith(":")) {
      filtersJoined = filtersJoined.substring(0, filtersJoined.length - 1);
    }

    digitalData.onsiteSearch.onsiteSearchInfo.onsiteSearchSecondaryFilter = filtersJoined;
    if (facetValueEnglishDisplayString == undefined) {
      this.niAnalytics.captureSearchFilter({'onsiteSearchSecondaryFilter': filtersJoined});
    } else {
      this.niAnalytics.captureSearchFilter({'onsiteSearchSecondaryFilter': filtersJoined, 'onsiteSearchScenario': facetValueEnglishDisplayString});
    }

    let tntSearchExperience = this.cookieService.get('tnt_search_experience');
    if (tntSearchExperience) {
      this.niAnalytics.setAndTrack("eVar112", `2855552|click|Experience ${tntSearchExperience}|Navigation change`);
      this.niAnalytics.sendBeacon({"asPageView": false, "linkType": "o", "linkName": "o"});
    }
  }

  trackSearchInstead(searchDym: SearchDym) {
    if (!!window.monitoringTool) {
      console.info("Analytics sending is refused because of Monitoring-Tool header: " + window.monitoringTool);
      return;
    }

    this.niAnalytics.captureSearchInstead({
      'onsiteSearchSearchInsteadFor': searchDym.originalTerm?.toLowerCase(),
      'onsiteSearchDym': searchDym.suggestedTerm?.toLowerCase()
    });

    let tntSearchExperience = this.cookieService.get('tnt_search_experience');
    if (tntSearchExperience) {
      this.niAnalytics.setAndTrack("eVar112", `2855552|click|Experience ${tntSearchExperience}|Did you mean`);
      this.niAnalytics.sendBeacon({"asPageView": false, "linkType": "o", "linkName": "o"});
    }
  }

  trackAddToCart(partNumber: string, price: string, currencyCode: string) {
    if (!!window.monitoringTool) {
      console.info("Analytics sending is refused because of Monitoring-Tool header: " + window.monitoringTool);
      return;
    }

    let cartId = this.cookieService.get('NI_B2B_ORDER_ID');
    if (cartId) {
      this.niAnalytics.setAndTrack("eVar22", cartId);
    } else {
      this.niAnalytics.setAndTrack("eVar28", "cart");
      this.niAnalytics.setAndTrack("eVar29", "cart start");
      this.niAnalytics.setAndTrack("scOpen", "");
    }

    this.niAnalytics.setAndTrack("eVar1", this.niAnalytics.getDataElement("PAGE_NAME"));
    this.niAnalytics.setAndTrack("eVar56", this.niAnalytics.getDataElement("DETAILED_PAGENAME"));
    this.niAnalytics.setAndTrack("eVar101", this.niAnalytics.getDataElement("HASHED_EMAIL:USER:DL"));
    this.niAnalytics.setAndTrack("eVar129", "Search Results PN Mashup");
    this.niAnalytics.setAndTrack("eVar147", new Date().toISOString());
    this.niAnalytics.setAndTrack("eVar152", this.niAnalytics.getDataElement("OneTrust_STATUS"));

    this.niAnalytics.setAndTrack("event17", price);
    this.niAnalytics.setAndTrack("scAdd", "");
    this.niAnalytics.setAndTrack("products", ";" + partNumber);
    this.niAnalytics.setAndTrack("currencyCode", currencyCode);

    let tntSearchExperience = this.cookieService.get('tnt_search_experience');
    if (tntSearchExperience) {
      this.niAnalytics.setAndTrack("eVar112", `2855552|click|Experience ${tntSearchExperience}|Add to cart`);
    }

    this.niAnalytics.sendBeacon({"asPageView": false, "linkType": "o", "linkName": "o"});
  }
}
